import type { AuthSession } from "@itaas/core/auth/models/session.model";
import { getUserPermissionKeys } from "@itaas/core/rbac/access";
import {
  isTenantAdminRole,
  resolveUserModuleGrants,
} from "@itaas/core/admin/module-grants";
import {
  resolveAccessibleModules,
  buildModuleHandoffUrl,
  pickPostLoginModule,
  saveLastModule,
  loadLastModule,
  loadReturnToModuleId,
  loadReturnToPath,
  clearReturnTo,
} from "@itaas/core/modules";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { getLicensedModulesFromSession } from "@/auth/suite-auth.service";
import { getModuleBaseUrl, getModuleDefaultPath } from "@/config";
import { canAccessPlatformHub, defaultPlatformPath } from "@/navigation/platform-access";
import { routes } from "@/router/all_routes";

export type PostLoginAction =
  | { kind: "route"; path: string }
  | { kind: "handoff"; url: string };

function moduleAccessOptions(session: AuthSession) {
  const roleSlug = session.user?.roles?.[0]?.slug;
  const tenantId = session.tenant?.id ?? "";
  const userId = session.user?.id ?? "";
  const userModuleGrants =
    tenantId && userId
      ? resolveUserModuleGrants(tenantId, userId, roleSlug)
      : null;
  return {
    isTenantAdmin: isTenantAdminRole(roleSlug) || Boolean(session.user?.isSuperAdmin),
    userModuleGrants,
  };
}

export function getPostLoginAction(): PostLoginAction {
  const session = loadStoredSession();
  if (!session) return { kind: "route", path: routes.login };

  const licensed = getLicensedModulesFromSession(session);
  const permissions = getUserPermissionKeys(session.user);
  const accessible = resolveAccessibleModules(
    licensed,
    permissions,
    undefined,
    moduleAccessOptions(session),
  );
  const returnToPath = loadReturnToPath();
  const returnToModuleId = loadReturnToModuleId();
  const decision = pickPostLoginModule(accessible, {
    returnToModuleId,
    lastModuleId: loadLastModule(),
  });

  clearReturnTo();

  if (
    !returnToPath &&
    !returnToModuleId &&
    session.user?.isSuperAdmin &&
    canAccessPlatformHub(session.user)
  ) {
    return { kind: "route", path: defaultPlatformPath(session.user) };
  }

  if (decision === "denied") {
    return { kind: "route", path: routes.accessDenied };
  }

  if (decision === "launcher") {
    return { kind: "route", path: routes.modules };
  }

  saveLastModule(decision.id);
  const baseUrl = getModuleBaseUrl(decision.id);
  const targetPath = returnToPath ?? getModuleDefaultPath(decision.id);
  const url = buildModuleHandoffUrl(
    baseUrl,
    decision.handoffPath,
    session,
    targetPath,
  );

  return { kind: "handoff", url };
}

export function navigateToModule(moduleId: string): void {
  const session = loadStoredSession();
  if (!session) {
    window.location.href = routes.login;
    return;
  }

  const licensed = getLicensedModulesFromSession(session);
  const permissions = getUserPermissionKeys(session.user);
  const accessible = resolveAccessibleModules(
    licensed,
    permissions,
    undefined,
    moduleAccessOptions(session),
  );
  const entry = accessible.find((m) => m.id === moduleId);

  if (!entry) {
    console.warn(`Module « ${moduleId} » inaccessible (licence ou permissions).`);
    return;
  }

  try {
    saveLastModule(moduleId);
    const baseUrl = getModuleBaseUrl(moduleId);
    const targetPath = getModuleDefaultPath(moduleId);
    const url = buildModuleHandoffUrl(
      baseUrl,
      entry.handoffPath,
      session,
      targetPath,
    );
    window.location.assign(url);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Ouverture du module impossible.";
    window.alert(message);
  }
}

export function getAccessibleModulesForSession(session: AuthSession | null) {
  if (!session) return [];
  const licensed = getLicensedModulesFromSession(session);
  const permissions = getUserPermissionKeys(session.user);
  return resolveAccessibleModules(
    licensed,
    permissions,
    undefined,
    moduleAccessOptions(session),
  );
}
